/**
 * Community Groups List Page
 * /community/groups - 모임 목록 페이지
 */

import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "wouter";
import { Search, Filter, Plus, MapPin, Users, Calendar, Eye } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PageContainer } from "@/components/layout/PageContainer";
import Seo from "@/components/seo/Seo";
import { formatDistanceToNow } from "date-fns";
import { ko } from "date-fns/locale";
import { useAuth } from "@/contexts/AuthContext";
import { useRegion } from "@/contexts/RegionContext";
import { australianStates } from "@shared/suburbs";

interface Group {
  id: number;
  title: string;
  slug: string;
  description: string;
  category_id: number;
  category_name: string;
  category_name_ko: string;
  category_icon: string;
  city: string;
  state: string;
  suburb: string | null;
  cover_image_url: string | null;
  member_count: number;
  event_count: number;
  view_count: number;
  created_at: string;
}

interface GroupCategory {
  id: number;
  slug: string;
  name: string;
  name_ko: string;
  icon: string;
}

export default function GroupsListPage() {
  const { user } = useAuth();
  const { selectedRegion } = useRegion();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [selectedState, setSelectedState] = useState<string>(selectedRegion || "ALL");
  const [sort, setSort] = useState<string>("newest");

  // 카테고리 목록 조회
  const { data: categoriesData } = useQuery<{ categories: GroupCategory[] }>({
    queryKey: ["/api/community/categories"],
    queryFn: async () => {
      const response = await fetch("/api/community/categories");
      if (!response.ok) throw new Error("Failed to fetch categories");
      return response.json();
    },
  });

  const categories = categoriesData?.categories || [];

  // 그룹 목록 조회
  const { data: groupsData, isLoading } = useQuery<{
    groups: Group[];
    pagination: { total: number; limit: number; offset: number; hasMore: boolean };
  }>({
    queryKey: ["/api/community/groups", selectedState, selectedCategory, searchQuery, sort],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (selectedState !== "ALL") {
        params.set("state", selectedState);
      }
      if (selectedCategory !== "all") {
        params.set("categoryId", selectedCategory);
      }
      if (searchQuery.trim()) {
        params.set("q", searchQuery.trim());
      }
      params.set("sort", sort);

      const response = await fetch(`/api/community/groups?${params}`);
      if (!response.ok) throw new Error("Failed to fetch groups");
      return response.json();
    },
  });

  const groups = groupsData?.groups || [];

  return (
    <>
      <Seo
        title="교민모임 — OZKNEWS"
        description="호주 한인 커뮤니티의 다양한 모임을 찾아보고 참여해보세요. 운동, 스터디, 취미, 동네친구 등 다양한 카테고리."
        path="/community/groups"
        keywords={["교민모임", "커뮤니티", "동네친구", "스터디", "운동", "취미", "호주 한인"]}
      />

      <div className="min-h-screen bg-background">
        {/* Search Section */}
        <section className="bg-background py-12 border-b border-border">
          <div className="max-w-7xl mx-auto px-4">
            <div className="max-w-4xl mx-auto space-y-6">
              {/* Category Tabs */}
              <div className="text-center">
                <div className="flex flex-wrap justify-center gap-2 mb-6">
                  <Button
                    variant={selectedCategory === "all" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setSelectedCategory("all")}
                    className="min-w-[80px]"
                  >
                    전체
                  </Button>
                  {categories.map((category) => (
                    <Button
                      key={category.id}
                      variant={selectedCategory === String(category.id) ? "default" : "outline"}
                      size="sm"
                      onClick={() => setSelectedCategory(String(category.id))}
                      className="min-w-[80px]"
                    >
                      {category.name_ko || category.name}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Search Bar */}
              <div className="flex flex-col sm:flex-row gap-3 items-stretch">
                <div className="flex-1 flex gap-2">
                  <Input
                    type="text"
                    placeholder="모임명 또는 설명으로 검색"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === "Enter" && searchQuery.trim()) {
                        // 검색 실행 (자동으로 쿼리 업데이트됨)
                      }
                    }}
                    className="flex-1 h-14 px-4 text-base bg-background border-border text-foreground placeholder:text-muted-foreground focus:bg-background focus:border-[#c066ac] dark:focus:border-[#9966cc]"
                  />
                  <Button
                    size="lg"
                    className="h-14 px-6 bg-[#c066ac] hover:bg-[#b055a0] text-white"
                  >
                    <Search className="h-5 w-5" />
                  </Button>
                </div>
              </div>

              {/* Filters */}
              <div className="flex flex-wrap gap-3 items-center">
                <Select value={selectedState} onValueChange={setSelectedState}>
                  <SelectTrigger className="w-[150px] bg-background border-border">
                    <SelectValue placeholder="지역 선택" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL">전체 지역</SelectItem>
                    {australianStates.map((state) => (
                      <SelectItem key={state.id} value={state.id}>
                        {state.nameKo}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={sort} onValueChange={setSort}>
                  <SelectTrigger className="w-[150px] bg-background border-border">
                    <SelectValue placeholder="정렬" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="newest">최신순</SelectItem>
                    <SelectItem value="popular">인기순</SelectItem>
                    <SelectItem value="upcoming">다가오는 일정순</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <PageContainer size="default">
          <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <h2 className="text-xl font-bold">교민모임</h2>
              {user && (
                <Button asChild>
                  <Link href="/community/groups/new">
                    <Plus className="w-4 h-4 mr-2" />
                    모임 만들기
                  </Link>
                </Button>
              )}
            </div>

            {/* Groups List */}
            {isLoading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {Array.from({ length: 6 }).map((_, i) => (
                  <Card key={i} className="p-6 animate-pulse">
                    <div className="h-48 bg-muted rounded-lg mb-4" />
                    <div className="h-5 bg-muted rounded w-3/4 mb-2" />
                    <div className="h-4 bg-muted rounded w-full mb-2" />
                    <div className="h-4 bg-muted rounded w-2/3" />
                  </Card>
                ))}
              </div>
            ) : groups.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {groups.map((group) => (
                  <Link key={group.id} href={`/community/groups/${group.id}`}>
                    <Card className="hover-elevate cursor-pointer overflow-hidden">
                      {group.cover_image_url && (
                        <div className="relative h-48 overflow-hidden">
                          <img
                            src={group.cover_image_url}
                            alt={group.title}
                            className="w-full h-full object-cover"
                          />
                          <div className="absolute top-3 right-3">
                            <Badge variant="secondary" className="bg-background/90 backdrop-blur">
                              {group.category_name_ko || group.category_name}
                            </Badge>
                          </div>
                        </div>
                      )}

                      <div className="p-6 space-y-4">
                        <div>
                          <h3 className="font-semibold text-lg mb-2 line-clamp-2">
                            {group.title}
                          </h3>
                          <p className="text-sm text-muted-foreground line-clamp-2">
                            {group.description}
                          </p>
                        </div>

                        <div className="space-y-2 text-sm">
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <MapPin className="w-4 h-4 text-[#663366] dark:text-[#9966cc]" />
                            <span>
                              {[group.suburb, group.city, group.state]
                                .filter(Boolean)
                                .join(", ")}
                            </span>
                          </div>

                          <div className="flex items-center gap-2 text-muted-foreground">
                            <Users className="w-4 h-4 text-[#663366] dark:text-[#9966cc]" />
                            <span>{group.member_count || 0}명</span>
                          </div>

                          <div className="flex items-center gap-2 text-muted-foreground">
                            <Calendar className="w-4 h-4 text-[#663366] dark:text-[#9966cc]" />
                            <span>{group.event_count || 0}개 일정</span>
                          </div>

                          <div className="flex items-center gap-2 text-muted-foreground">
                            <Eye className="w-4 h-4 text-[#663366] dark:text-[#9966cc]" />
                            <span>{group.view_count || 0}</span>
                          </div>
                        </div>
                      </div>
                    </Card>
                  </Link>
                ))}
              </div>
            ) : (
              <Card className="p-12 text-center">
                <div className="flex flex-col items-center gap-4">
                  <Users className="w-12 h-12 text-muted-foreground" />
                  <div>
                    <h3 className="font-semibold text-lg mb-2">모임이 없습니다</h3>
                    <p className="text-muted-foreground mb-4">
                      새로운 모임을 만들어 커뮤니티에 참여해보세요!
                    </p>
                    {user && (
                      <Button asChild>
                        <Link href="/community/groups/new">
                          <Plus className="w-4 h-4 mr-2" />
                          첫 모임 만들기
                        </Link>
                      </Button>
                    )}
                  </div>
                </div>
              </Card>
            )}

            {/* Pagination */}
            {groupsData?.pagination.hasMore && (
              <div className="text-center">
                <Button variant="outline" size="lg">
                  더 보기
                </Button>
              </div>
            )}
          </div>
        </PageContainer>
      </div>
    </>
  );
}

